/* ===========================================================================
   Pressing Guide controller.
   Owns NO copy and NO numbers — every value is read from the server-rendered
   DOM (block-driven data-* attributes). Two independent, guarded IIFEs:
     1) the press engine (tabs + material listbox + pulse)
   UK: Celsius only. The °F/°C unit toggle and every Fahrenheit code path are
   deliberately absent — do not reintroduce them. See the UK pressing-guide note.
     2) the jump nav (always-fixed via CSS; this file only drives scroll-spy)
   Each section loads this file; init guards make the second execution a no-op.
   See .claude/rules/pressing-guide.md.
   =========================================================================== */
(function () {
  'use strict';
  var toArray = function (nl) { return Array.prototype.slice.call(nl); };
  var reduceMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ------------------------------------------------------------ PAGE SCROLL
     ⚠ Lenis owns page scroll on this store (window.ncLenis, from nc-smooth.js).
     Anything that moves the VIEWPORT must go through it — a native
     scrollIntoView/scrollTo runs its own animation alongside Lenis's and the two
     fight (this is what made the page floaty; see the block at the top of
     pressing-guide.css). Same pattern already used in gangsheet-uk-postupload.

     ⚠ Lenis does NOT read CSS scroll-margin-top, which is what lands these
     targets clear of the sticky header (110px desktop / 24px mobile, tuned in
     pressing-guide.css). So read it off the element and pass it as an explicit
     negative offset — keeping the CSS as the single source of truth rather than
     hardcoding the number here.

     Falls back to native scrollIntoView when Lenis is absent (mobile/touch and
     prefers-reduced-motion, where nc-smooth.js deliberately does not start it),
     so this is correct with or without Lenis. */
  function pgScrollToEl(el) {
    if (!el) return;
    var instant = reduceMotion();
    var lenis = window.ncLenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      var margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
      lenis.scrollTo(el, { offset: -margin, immediate: instant });
      return;
    }
    el.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'start' });
  }

  /* ---------------------------------------------------------------- ENGINE */
  (function () {
    var root = document.querySelector('[data-pressing-engine]');
    if (!root || root.__pgEngineInit) return;
    root.__pgEngineInit = true;

    var tempEl = root.querySelector('[data-temp-value]');
    var presEl = root.querySelector('[data-pressure-value]');
    var timeEl = root.querySelector('[data-time-value]');

    function activeTab() {
      return root.querySelector('.pg-tab[aria-selected="true"]') || root.querySelector('.pg-tab');
    }
    function currentOption() {
      return root.querySelector('.pg-select-option[aria-selected="true"]') || root.querySelector('.pg-select-option');
    }
    function fmtTime(t) {
      t = String(t || '');
      return '~' + (/s$/.test(t) ? t.replace(/s$/, ' seconds') : t);
    }

    // Recompute settings card + inline chips from the selected material + active tab.
    function applyMaterial(opt, pulse) {
      if (!opt) return;
      var tab = activeTab();
      var mC = opt.getAttribute('data-temp-c') || '';
      var presOverride = tab && tab.getAttribute('data-pressure-override');
      var timeOverride = tab && tab.getAttribute('data-time-override');
      var effPres = presOverride ? presOverride : (opt.getAttribute('data-pressure') || '');
      var effTime = timeOverride ? timeOverride : (opt.getAttribute('data-time') || '');

      if (tempEl) { tempEl.setAttribute('data-c', mC); tempEl.textContent = mC; }
      if (presEl) presEl.textContent = effPres;
      if (timeEl) timeEl.textContent = effTime;

      // chips live in the separate Steps section — query the whole document
      document.querySelectorAll('.material-temp').forEach(function (el) {
        el.setAttribute('data-c', mC);
        el.textContent = mC;
      });
      var ts = fmtTime(effTime);
      document.querySelectorAll('.material-time').forEach(function (el) { el.textContent = ts; });

      if (pulse) pulseSettings();
    }

    function pulseSettings() {
      if (reduceMotion()) return;
      root.querySelectorAll('.pg-setting').forEach(function (el) {
        el.classList.remove('pg-pulse');
        void el.offsetWidth; // force reflow so the animation can replay
        el.classList.add('pg-pulse');
      });
    }

    // Tab → swap the visible notes + step bodies for that heat source, then refill chips.
    function switchSource(tab) {
      var key = tab.getAttribute('data-source');
      // notes are in this (engine) section; step bodies are in the separate Steps section
      root.querySelectorAll('[data-notes-for]').forEach(function (ul) {
        ul.hidden = ul.getAttribute('data-notes-for') !== key;
      });
      document.querySelectorAll('[data-step-source]').forEach(function (b) {
        b.hidden = b.getAttribute('data-step-source') !== key;
      });
      // per-source video players live in the separate Pressing Video section
      document.querySelectorAll('[data-video-source]').forEach(function (b) {
        b.hidden = b.getAttribute('data-video-source') !== key;
      });
      // dynamic settings heading: "Find Your <active source> Settings" — reads the active card's label (one source of truth). Runs on load (default source) + every user switch.
      var settingsSrc = document.querySelector('[data-settings-source]');
      var tabLabel = tab.querySelector('.pg-tile-label');
      if (settingsSrc && tabLabel) settingsSrc.textContent = tabLabel.textContent.trim();
      applyMaterial(currentOption(), false);
    }

    /* heat-source tabs: role=tab + roving tabindex + arrow-key nav */
    var tabs = toArray(root.querySelectorAll('.pg-tab'));
    function selectTab(tab) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      switchSource(tab);
      // auto-scroll to the settings section on USER selection (not on load — switchSource
      // runs on init, selectTab does not). Routed through pgScrollToEl so Lenis performs the
      // move; #settings' scroll-margin-top still supplies the sticky-header offset, and
      // reduced motion is handled inside the helper.
      var settings = document.getElementById('settings');
      if (settings) pgScrollToEl(settings);
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectTab(tab); });
      tab.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var dir = e.key === 'ArrowRight' ? 1 : -1;
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          next.focus();
          selectTab(next);
        }
      });
    });

    /* UK: the °F/°C toggle handler that stood here is deliberately removed — this
       store is Celsius only, so there is no unit to switch and no data-f to read.
       Temperatures are written once by applyMaterial(). Do not reintroduce. */

    /* material picker — accessible single-select listbox (ARIA APG pattern).
       This is a deliberate a11y upgrade over the source mockup, whose dropdown
       was mouse-only. Full keyboard: ↓/↑/Home/End/Enter/Space/Esc + type-ahead. */
    var select = root.querySelector('[data-pg-select]');
    if (select) {
      var trigger = select.querySelector('.pg-select-trigger');
      var listbox = select.querySelector('.pg-select-list');
      var opts = toArray(select.querySelectorAll('.pg-select-option'));
      var activeIdx = Math.max(0, opts.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; }));
      var typeBuf = '', typeTimer = null;

      function openList() {
        select.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
        setActive(opts[activeIdx] || opts[0]);
        listbox.focus();
      }
      function closeList(focusBack) {
        select.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
        if (focusBack) trigger.focus();
      }
      function setActive(opt) {
        if (!opt) return;
        activeIdx = opts.indexOf(opt);
        opts.forEach(function (o) { o.classList.toggle('pg-active', o === opt); });
        listbox.setAttribute('aria-activedescendant', opt.id);
        /* Deliberately NOT routed through pgScrollToEl. .pg-select-list is its own scroll
           container (max-height:340px; overflow-y:auto), so this scrolls the LISTBOX, not the
           viewport — Lenis owns page scroll only, and handing it an inner-element scroll would
           turn keyboard navigation inside the picker into a page jump. Native is correct here,
           and it is instant now that the global smooth rule is gone. */
        opt.scrollIntoView({ block: 'nearest' });
      }
      function choose(opt) {
        if (!opt) return;
        opts.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
        opt.setAttribute('aria-selected', 'true');
        var img = opt.querySelector('img'), name = opt.querySelector('.pg-opt-name');
        var tImg = trigger.querySelector('.pg-select-thumb'), tName = trigger.querySelector('.pg-select-name');
        if (img && tImg) tImg.src = img.src;
        if (name && tName) tName.textContent = name.textContent;
        // trigger "Most Popular" badge follows the selection (element is server-rendered; we only toggle [hidden])
        var tBadge = trigger.querySelector('[data-popular-badge]');
        if (tBadge) tBadge.hidden = !opt.querySelector('.pg-badge-popular');
        closeList(true);
        applyMaterial(opt, true);
      }
      function typeAhead(ch) {
        if (typeTimer) clearTimeout(typeTimer);
        typeBuf += ch.toLowerCase();
        typeTimer = setTimeout(function () { typeBuf = ''; }, 600);
        var match = opts.find(function (o) {
          var n = o.querySelector('.pg-opt-name');
          return n && n.textContent.toLowerCase().indexOf(typeBuf) === 0;
        });
        if (match) setActive(match);
      }

      trigger.addEventListener('click', function () {
        select.classList.contains('open') ? closeList(false) : openList();
      });
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); openList();
        }
      });
      listbox.addEventListener('keydown', function (e) {
        switch (e.key) {
          case 'ArrowDown': e.preventDefault(); setActive(opts[Math.min(activeIdx + 1, opts.length - 1)]); break;
          case 'ArrowUp': e.preventDefault(); setActive(opts[Math.max(activeIdx - 1, 0)]); break;
          case 'Home': e.preventDefault(); setActive(opts[0]); break;
          case 'End': e.preventDefault(); setActive(opts[opts.length - 1]); break;
          case 'Enter': case ' ': e.preventDefault(); choose(opts[activeIdx]); break;
          case 'Escape': e.preventDefault(); closeList(true); break;
          case 'Tab': closeList(false); break;
          default: if (e.key.length === 1) typeAhead(e.key);
        }
      });
      opts.forEach(function (o) {
        o.addEventListener('click', function () { choose(o); });
        o.addEventListener('mousemove', function () { setActive(o); });
      });
      document.addEventListener('click', function (e) {
        if (!select.contains(e.target)) closeList(false);
      });
    }
    // Steps are a separate section now: on load, sync the cross-section step bodies
    // to the active tab and overwrite the SSR'd no-JS default chips with the engine's
    // live default material. (Notes + settings card are already SSR-correct.)
    var initTab = activeTab();
    if (initTab) switchSource(initTab);
  })();

  /* -------------------------------------------------------------- JUMP NAV */
  (function () {
    var nav = document.querySelector('[data-pressing-jumpnav]');
    if (!nav || nav.__pgNavInit) return;
    nav.__pgNavInit = true;
    var links = toArray(nav.querySelectorAll('.pg-jump-link[data-target]'));

    // The bar is always position:fixed via CSS (no in-flow phase, no sentinel/spacer).
    // This IIFE only drives scroll-spy (active-tab highlighting).
    var targets = links.map(function (l) {
      var sel = l.getAttribute('data-target'), el = null;
      try { el = sel && document.querySelector(sel); } catch (_) {}
      return { link: l, el: el };
    }).filter(function (t) { return t.el; });

    function spy() {
      if (!targets.length) return;
      var bar = nav.querySelector('.pg-jump');
      var off = (bar ? bar.getBoundingClientRect().bottom : 150) + 24; // live fixed-nav bottom + buffer; clears the 168px desktop / 64px mobile scroll-margin landings so the clicked section registers at both breakpoints
      var cur = targets[0];
      targets.forEach(function (t) {
        if (t.el.getBoundingClientRect().top <= off) cur = t;
      });
      links.forEach(function (l) { l.classList.remove('active'); });
      if (cur) cur.link.classList.add('active');
    }
    window.addEventListener('scroll', spy, { passive: true });
    spy();
  })();

  /* ------------------------------------------ INLINE JUMP NAV (settings-card) */
  /* The relocated inline jump links (.pg-jump-inline) keep real-anchor hrefs, but a 3rd-party
     app injects a stray </body></html> mid-page that breaks NATIVE fragment scrolling on mobile
     for the upper targets (#video / #steps don't scroll; #troubleshooting, on the far side of the
     malformed close, does). getElementById still resolves all three (verified [1,1,1] live), so we
     override the broken native scroll with a scripted scroll, which is immune to the malformed DOM.
     The hrefs stay as the no-JS / crawlable fallback; this just preventDefaults + scrolls.
     Routed through pgScrollToEl so Lenis owns the movement (UK); the targets' scroll-margin-top
     (.pg-anchor / .pg-steps-heading: 110px desktop / 24px mobile) still supplies the offset — the
     helper reads it off the element. Harmless on desktop (native nav already works). */
  (function () {
    if (window.__pgInlineNavInit) return;
    window.__pgInlineNavInit = true;
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest && e.target.closest('.pg-jump-inline__link');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();
      pgScrollToEl(target);
    });
  })();
})();
